import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Coordinates } from '@lt/api/dist/types/map';
import type { MapState, setActiveHotelType, setPopupDataType } from './types';
import { hotelHoverAction } from '../hotels/actions';
import { filtrationSuccessAction } from '../filters/actions';
import type { FiltrationSuccess } from '../filters/types';

const initialState: MapState = {
  hotels: undefined,
  hotelsInList: [],
  bounds: undefined,
  activeHotel: undefined,
  expanded: false,
  destinationBounds: null,
  popupData: undefined,
};

const getState = (state) =>
  typeof state.toJS === 'function' ? state.toJS() : state;

const map = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setPopupData: (state, action: setPopupDataType) => {
      const { id, coordinates, visited } = action.payload;
      return {
        ...getState(state),
        popupData: action.payload,
        activeHotel: { id: Number(id), coordinates, visited },
      };
    },
    resetPopupData: (state) => ({
      ...getState(state),
      popupData: initialState.popupData,
      activeHotel: initialState.activeHotel,
    }),
    toggleMap: (state) => ({
      ...getState(state),
      expanded: !state.expanded,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(hotelHoverAction, (state, action: setActiveHotelType) => {
      if (state.popupData)
        return {
          ...getState(state),
          popupData: initialState.popupData,
          activeHotel: action.payload,
        };

      return {
        ...getState(state),
        activeHotel: action.payload,
      };
    });
    builder.addCase(
      filtrationSuccessAction,
      (state, action: PayloadAction<FiltrationSuccess>) => {
        const { hotels } = action.payload;
        if (!hotels.length)
          return {
            ...getState(state),
            hotelsInList: [],
            hotels: [],
          };

        const hotelsInList = hotels.map((hotelGroup) => hotelGroup.hotel.id);
        const responseHotels = hotels.reduce((obj, hotel) => {
          obj[hotel.hotel.id] = {
            long: hotel.hotel.long,
            lat: hotel.hotel.lat,
          };
          return obj;
        }, {});
        const activeHotel = {
          index: 0,
          id: hotels[0]?.hotel?.id,
          coordinates: [
            hotels[0]?.hotel?.long,
            hotels[0]?.hotel?.lat,
          ] as Coordinates,
        };

        return {
          ...getState(state),
          hotelsInList,
          hotels: responseHotels,
          activeHotel,
        };
      },
    );
  },
});

export const { setPopupData, resetPopupData, toggleMap } = map.actions;

export const { name } = map;
export { initialState };

export default map.reducer;
